import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../../../utils/api';
import Pagination, { PAGE_SIZE } from '../../../../components/portal/Pagination';
import {
  RiAddLine, RiCalendarCheckLine, RiEditLine, RiDeleteBinLine,
  RiSaveLine, RiCloseLine, RiFilterLine, RiRefreshLine, RiCalendarLine,
} from 'react-icons/ri';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const LEAVE_TYPES = [
  { value: 'full_day',    label: 'Full Day'    },
  { value: 'first_half',  label: 'First Half'  },
  { value: 'second_half', label: 'Second Half' },
];

const LEAVE_STATUS_STYLE = {
  pending:  'badge text-[#f59e0b] bg-[rgba(245,158,11,0.08)] border-[rgba(245,158,11,0.2)]',
  approved: 'badge text-[#10b981] bg-[rgba(16,185,129,0.08)] border-[rgba(16,185,129,0.2)]',
  rejected: 'badge text-red-500 bg-red-50 border-red-100',
};

const EMPTY_FORM = { leave_type: 'full_day', start_date: '', end_date: '', reason: '' };
const labelCls   = 'block text-xs font-semibold text-[#6B7280] mb-1.5 uppercase tracking-wide';

export default function LeaveRequestTab() {
  const today = new Date();

  // ── form ──────────────────────────────────────────────────────────────────
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError,  setFormError]  = useState('');

  // ── server-side filters ───────────────────────────────────────────────────
  const [filterMonth, setFilterMonth] = useState(today.getMonth() + 1);
  const [filterYear,  setFilterYear]  = useState(today.getFullYear());
  const [filterDate,  setFilterDate]  = useState('');   // specific date overrides month

  // ── client-side filter ────────────────────────────────────────────────────
  const [filterStatus, setFilterStatus] = useState('all');

  // ── data ──────────────────────────────────────────────────────────────────
  const [leaves,     setLeaves]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [listError,  setListError]  = useState('');
  const [editingId,  setEditingId]  = useState(null);
  const [editForm,   setEditForm]   = useState(EMPTY_FORM);
  const [deletingId, setDeletingId] = useState(null);


  // ── pagination ────────────────────────────────────────────────────────────
  const [page,  setPage]  = useState(1);
  const [total, setTotal] = useState(0);

  // ── load ──────────────────────────────────────────────────────────────────
  const load = useCallback(() => {
    setLoading(true);
    setListError('');
    const params = { page, limit: PAGE_SIZE };
    if (filterDate) {
      params.date = filterDate;
    } else if (filterMonth && filterYear) {
      params.month = filterMonth;
      params.year  = filterYear;
    }
    // else: no date/month/year sent → backend returns all leaves (no pagination)
    if (filterStatus !== 'all') params.status = filterStatus;

    api.get('/leaves/mine', { params })
      .then((res) => {
        const d = res.data;
        const list = Array.isArray(d) ? d : d?.leaves || d?.data || [];
        setLeaves(list);
        setTotal(d?.meta?.total ?? list.length);
      })
      .catch(() => setListError('Failed to load leaves.'))
      .finally(() => setLoading(false));
  }, [filterMonth, filterYear, filterDate, filterStatus, page]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [filterMonth, filterYear, filterDate, filterStatus]);


  // Status filter is server-side, so the loaded leaves are already filtered.
  const displayed = leaves;

  // ── stats ─────────────────────────────────────────────────────────────────
  // `total` is server-side (whole filter set). Per-status counts are computed from current page.
  const stats = useMemo(() => ({
    total,
    pending:      leaves.filter((l) => l.status === 'pending').length,
    approved:     leaves.filter((l) => l.status === 'approved').length,
    approvedDays: leaves.filter((l) => l.status === 'approved')
                        .reduce((s, l) => s + (Number(l.total_days) || 0), 0).toFixed(1),
  }), [leaves, total]);

  // ── form helpers ──────────────────────────────────────────────────────────
  const buildPayload = (f) => {
    const p = { leave_type: f.leave_type, start_date: f.start_date, reason: f.reason };
    if (f.leave_type === 'full_day' && f.end_date) p.end_date = f.end_date;
    return p;
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setFormError('');

    // Client-side date validation
    if (!form.start_date) {
      setFormError('Start date is required.');
      return;
    }
    if (form.leave_type === 'full_day' && form.end_date && form.end_date < form.start_date) {
      setFormError('End date cannot be before start date.');
      return;
    }

    setSubmitting(true);
    try {
      const res     = await api.post('/leaves', buildPayload(form));
      const created = res.data?.leave || res.data?.data || res.data;
      // prepend to list only if it falls in the viewed period
      const sd = new Date(form.start_date);
      const inView = filterDate
        ? form.start_date === filterDate
        : (sd.getMonth() + 1 === filterMonth && sd.getFullYear() === filterYear);
      if (inView) setLeaves((prev) => [created, ...prev]);
      setForm(EMPTY_FORM);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to apply for leave.');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit  = (l) => {
    setEditingId(l._id || l.id);
    setEditForm({
      leave_type: l.leave_type || 'full_day',
      start_date: (l.start_date || '').slice(0, 10),
      end_date:   (l.end_date   || '').slice(0, 10),
      reason:     l.reason || '',
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm(EMPTY_FORM); };

  const handleEdit = async (id) => {
    setListError('');
    try {
      const res     = await api.put(`/leaves/${id}`, buildPayload(editForm));
      const updated = res.data?.leave || res.data?.data || res.data;
      setLeaves((prev) =>
        prev.map((l) => (l._id || l.id) === id
          ? (updated && typeof updated === 'object' ? updated : { ...l, ...buildPayload(editForm) })
          : l)
      );
      cancelEdit();
    } catch (err) {
      setListError(err.response?.data?.message || 'Failed to update leave.');
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/leaves/${id}`);
      setLeaves((prev) => prev.filter((l) => (l._id || l.id) !== id));
    } catch (err) {
      setListError(err.response?.data?.message || 'Failed to delete leave.');
    } finally {
      setDeletingId(null);
    }
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  const lbl = (type) => LEAVE_TYPES.find((t) => t.value === type)?.label || type;

  const activeLabel = filterDate
    ? new Date(filterDate + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : (filterMonth && filterYear)
      ? `${MONTH_NAMES[filterMonth - 1]} ${filterYear}`
      : 'All time';

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">

      {/* ── LEFT: Apply form ─────────────────────────────────────────────── */}
      <div className="xl:col-span-2 space-y-4">
        <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#F3F4F6]" style={{ background: 'linear-gradient(135deg,rgba(9,132,227,0.04),rgba(213,0,249,0.04))' }}>
            <h3 className="text-[#1F2937] font-bold flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#0984e3,#d500f9)' }}>
                <RiAddLine size={14} className="text-white" />
              </div>
              Apply for Leave
            </h3>
            <p className="text-[#9CA3AF] text-xs mt-1">Submit a leave request for admin approval.</p>
          </div>

          <div className="p-6">
            {formError && (
              <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center justify-between">
                {formError}
                <button onClick={() => setFormError('')} className="ml-2 cursor-pointer flex-shrink-0"><RiCloseLine size={14} /></button>
              </div>
            )}
            <form onSubmit={handleApply} className="space-y-4">
              <div>
                <label className={labelCls} htmlFor="ltype">Leave Type</label>
                <select
                  id="ltype" value={form.leave_type}
                  onChange={(e) => setForm({ ...form, leave_type: e.target.value, end_date: '' })}
                  className="input-field cursor-pointer"
                >
                  {LEAVE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className={`grid gap-4 ${form.leave_type === 'full_day' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div>
                  <label className={labelCls} htmlFor="lstart">Start Date *</label>
                  <input id="lstart" type="date" required value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="input-field" />
                </div>
                {form.leave_type === 'full_day' && (
                  <div>
                    <label className={labelCls} htmlFor="lend">
                      End Date <span className="text-[#9CA3AF] normal-case font-normal tracking-normal">(optional)</span>
                    </label>
                    <input id="lend" type="date" min={form.start_date} value={form.end_date}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                      className="input-field" />
                  </div>
                )}
              </div>
              <div>
                <label className={labelCls} htmlFor="lreason">Reason *</label>
                <textarea id="lreason" required rows={3} value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="Brief reason for your leave request…"
                  className="input-field resize-none" />
              </div>
              <button type="submit" disabled={submitting}
                className="btn btn-primary w-full justify-center flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ padding: '0.65rem 1.2rem', fontSize: '13px' }}>
                {submitting
                  ? <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                  : <RiAddLine size={15} />}
                {submitting ? 'Submitting…' : 'Apply for Leave'}
              </button>
            </form>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Total',         value: stats.total,        color: '#0984e3' },
            { label: 'Pending',       value: stats.pending,      color: '#f59e0b' },
            { label: 'Approved',      value: stats.approved,     color: '#10b981' },
            { label: 'Approved Days', value: stats.approvedDays, color: '#8b5cf6' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border border-[#E5E7EB] rounded-xl p-4 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
              <div className="w-2 h-2 rounded-full mb-2 flex-shrink-0" style={{ background: color }} />
              <p className="text-[#1F2937] text-xl font-bold">{loading ? '—' : value}</p>
              <p className="text-[#9CA3AF] text-xs mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── RIGHT: Leave history with filters ────────────────────────────── */}
      <div className="xl:col-span-3 bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden flex flex-col">

        {/* filter header */}
        <div className="px-6 py-4 border-b border-[#F3F4F6] space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <RiFilterLine size={14} className="text-[#0984e3]" />
              <div>
                <h3 className="text-[#1F2937] font-bold text-sm">My Leave Requests</h3>
                <p className="text-[#9CA3AF] text-xs">
                  {loading ? 'Loading…' : `${total} request${total !== 1 ? 's' : ''} — ${activeLabel}`}
                </p>
              </div>
            </div>
            <button
              onClick={load} title="Refresh"
              className="w-7 h-7 rounded-lg border border-[#E5E7EB] flex items-center justify-center text-[#6B7280] hover:text-[#0984e3] hover:border-[#0984e3] transition-all cursor-pointer flex-shrink-0"
            >
              <RiRefreshLine size={13} />
            </button>
          </div>

          {/* Month + specific-date filters */}
          <div className="grid grid-cols-2 gap-2 max-w-md">
            {/* Month / Year — clearable. Empty = show ALL leaves. */}
            <div>
              <label className="block text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">
                Month <span className="normal-case font-normal text-[#C4C9D4]">(clear = all)</span>
              </label>
              <div className="flex items-center gap-1">
                <div className="relative flex-1 min-w-0">
                  <RiCalendarLine className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" size={12} />
                  <input
                    type="month"
                    value={filterMonth && filterYear ? `${filterYear}-${String(filterMonth).padStart(2, '0')}` : ''}
                    min="2005-01"
                    onChange={(e) => {
                      const [y, m] = (e.target.value || '').split('-');
                      if (y && m) {
                        setFilterYear(Number(y));
                        setFilterMonth(Number(m));
                        setFilterDate('');
                      } else {
                        setFilterMonth(null);
                        setFilterYear(null);
                      }
                    }}
                    className="input-field pl-7 text-xs py-1.5 w-full cursor-pointer"
                  />
                </div>
                {(filterMonth || filterYear) && (
                  <button
                    onClick={() => { setFilterMonth(null); setFilterYear(null); }}
                    className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 border border-[#E5E7EB] transition-all cursor-pointer"
                    title="Clear — show all leaves"
                  >
                    <RiCloseLine size={13} />
                  </button>
                )}
              </div>
            </div>

            {/* Specific date (overrides month + year) */}
            <div>
              <label className="block text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">Date</label>
              <div className="flex items-center gap-1">
                <div className="relative flex-1 min-w-0">
                  <RiCalendarLine className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" size={12} />
                  <input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    className="input-field pl-7 text-xs py-1.5 w-full cursor-pointer"
                  />
                </div>
                {filterDate && (
                  <button
                    onClick={() => setFilterDate('')}
                    className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 border border-[#E5E7EB] transition-all cursor-pointer"
                    title="Clear date — show whole month"
                  >
                    <RiCloseLine size={13} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Status pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {['all', 'pending', 'approved', 'rejected'].map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all cursor-pointer capitalize ${
                  filterStatus === s
                    ? 'text-white border-transparent'
                    : 'text-[#6B7280] border-[#E5E7EB] hover:border-[#0984e3] hover:text-[#0984e3]'
                }`}
                style={filterStatus === s ? { background: 'linear-gradient(135deg,#0984e3,#d500f9)' } : {}}
              >
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                {filterStatus === s && !loading && (
                  <span className="ml-1 opacity-70">{total}</span>
                )}
              </button>
            ))}
          </div>

          {/* Active pill */}
          {filterDate && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#9CA3AF]">Date:</span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full text-[#0984e3] bg-[rgba(9,132,227,0.08)] border border-[rgba(9,132,227,0.2)]">
                <RiCalendarLine size={11} />
                {fmtDate(filterDate + 'T00:00:00')}
                <button onClick={() => setFilterDate('')} className="ml-0.5 cursor-pointer"><RiCloseLine size={11} /></button>
              </span>
            </div>
          )}
        </div>

        {/* list error */}
        {listError && (
          <div className="mx-6 mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center justify-between">
            {listError}
            <button onClick={() => setListError('')} className="cursor-pointer ml-2"><RiCloseLine size={14} /></button>
          </div>
        )}

        {/* list body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="divide-y divide-[#F3F4F6]">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="px-6 py-4 space-y-2">
                  <div className="h-4 bg-[#F3F4F6] rounded w-1/3 animate-pulse" />
                  <div className="h-3 bg-[#F3F4F6] rounded w-1/2 animate-pulse" />
                </div>
              ))}
            </div>
          ) : displayed.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-14 h-14 rounded-2xl bg-[#F3F4F6] flex items-center justify-center">
                <RiCalendarCheckLine size={26} className="text-[#D1D5DB]" />
              </div>
              <div className="text-center">
                <p className="text-[#374151] text-sm font-semibold">
                  {filterStatus !== 'all'
                    ? `No ${filterStatus} leaves for ${activeLabel}`
                    : `No leaves for ${activeLabel}`}
                </p>
                <p className="text-[#9CA3AF] text-xs mt-1">Apply from the form on the left.</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-[#F3F4F6]">
              {displayed.map((l) => {
                const id         = l._id || l.id;
                const isPending  = l.status === 'pending';
                const isEditing  = editingId === id;
                const isDeleting = deletingId === id;

                return (
                  <div key={id} className="px-6 py-4 hover:bg-[#FAFBFC] transition-colors">
                    {isEditing ? (
                      <div className="space-y-3">
                        <div>
                          <label className={labelCls}>Leave Type</label>
                          <select value={editForm.leave_type}
                            onChange={(e) => setEditForm({ ...editForm, leave_type: e.target.value, end_date: '' })}
                            className="input-field text-sm cursor-pointer">
                            {LEAVE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                        <div className={`grid gap-3 ${editForm.leave_type === 'full_day' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                          <div>
                            <label className={labelCls}>Start Date *</label>
                            <input type="date" required value={editForm.start_date}
                              onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                              className="input-field text-sm" />
                          </div>
                          {editForm.leave_type === 'full_day' && (
                            <div>
                              <label className={labelCls}>End Date <span className="text-[#9CA3AF] normal-case font-normal tracking-normal">(optional)</span></label>
                              <input type="date" min={editForm.start_date} value={editForm.end_date}
                                onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                                className="input-field text-sm" />
                            </div>
                          )}
                        </div>
                        <div>
                          <label className={labelCls}>Reason *</label>
                          <textarea required rows={2} value={editForm.reason}
                            onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                            className="input-field text-sm resize-none" />
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleEdit(id)}
                            disabled={!editForm.start_date || !editForm.reason.trim()}
                            className="btn btn-primary flex items-center gap-1.5 disabled:opacity-50 text-xs"
                            style={{ padding: '0.45rem 0.9rem' }}>
                            <RiSaveLine size={13} /> Save
                          </button>
                          <button onClick={cancelEdit}
                            className="btn flex items-center gap-1.5 text-[#6B7280] text-xs"
                            style={{ padding: '0.45rem 0.9rem' }}>
                            <RiCloseLine size={13} /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="text-[#1F2937] text-sm font-semibold">{lbl(l.leave_type)}</p>
                            <span className={LEAVE_STATUS_STYLE[l.status] || 'badge capitalize'}>{l.status}</span>
                            {l.total_days != null && (
                              <span className="badge text-[#0984e3] bg-[rgba(9,132,227,0.08)] border-[rgba(9,132,227,0.2)]">
                                {l.total_days} day{l.total_days !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <p className="text-[#9CA3AF] text-xs flex items-center gap-1 mb-1">
                            <RiCalendarLine size={11} />
                            {fmtDate(l.start_date)}
                            {l.end_date && l.end_date !== l.start_date ? ` → ${fmtDate(l.end_date)}` : ''}
                          </p>
                          {l.reason && (
                            <p className="text-[#6B7280] text-xs leading-relaxed line-clamp-2">{l.reason}</p>
                          )}
                          {l.rejection_reason && (
                            <div className="mt-2 flex items-start gap-2 px-3 py-2.5 rounded-xl border border-red-200 bg-gradient-to-r from-red-50 to-rose-50">
                              <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-px">
                                <RiCloseLine size={11} className="text-red-500" />
                              </div>
                              <div>
                                <p className="text-[10px] font-bold text-red-400 uppercase tracking-wide mb-0.5">Rejection Reason</p>
                                <p className="text-red-600 text-xs leading-relaxed">{l.rejection_reason}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        {isPending && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button onClick={() => startEdit(l)}
                              className="p-1.5 text-[#9CA3AF] hover:text-[#0984e3] hover:bg-[rgba(9,132,227,0.08)] rounded-lg transition-all cursor-pointer"
                              aria-label="Edit">
                              <RiEditLine size={14} />
                            </button>
                            <button onClick={() => handleDelete(id)} disabled={isDeleting}
                              className="p-1.5 text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer disabled:opacity-50"
                              aria-label="Delete">
                              {isDeleting
                                ? <span className="w-3.5 h-3.5 border border-red-400 border-t-transparent rounded-full animate-spin block" />
                                : <RiDeleteBinLine size={14} />}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* pagination */}
        <Pagination page={page} total={total} onChange={setPage} />
      </div>
    </div>
  );
}
